import React from 'react';
import { Link } from 'react-router-dom';
import { useTrialStatus } from '../../hooks/useTrialStatus';
import { trackEvent } from '../../lib/analytics';

const TIER_LABEL: Record<string, string> = {
  sapling: 'Sapling',
  sprout: 'Sprout',
  canopy: 'Canopy',
};

/**
 * Slim banner shown at the top of the authenticated staff app while a paid
 * tier trial is active. Renders nothing when no trial is in progress.
 */
export const TrialBanner: React.FC = () => {
  const { isTrialActive, trialTier, daysRemaining } = useTrialStatus();

  if (!isTrialActive || !trialTier) {
    return null;
  }

  const tierLabel = TIER_LABEL[trialTier] ?? trialTier;
  const dayWord = daysRemaining === 1 ? 'day' : 'days';

  return (
    <div
      role="status"
      data-testid="trial-banner"
      className="bg-primary-600 dark:bg-primary-700 text-white text-sm"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-center">
        <span>
          Your <strong className="font-semibold">{tierLabel}</strong> trial
          ends in <strong className="font-semibold">{daysRemaining} {dayWord}</strong>
        </span>
        <span aria-hidden="true" className="opacity-70">·</span>
        <Link
          to="/pricing"
          onClick={() =>
            trackEvent('trial_banner_clicked', {
              trial_tier: trialTier,
              days_remaining: daysRemaining,
            })
          }
          className="underline font-semibold hover:text-primary-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-600 rounded-sm"
        >
          Upgrade now &rarr;
        </Link>
      </div>
    </div>
  );
};

export default TrialBanner;
